
import React from 'react';
import { PAYMENT_CONFIG, formatCurrency, formatViews } from '../lib/paymentConfig';

const Steps: React.FC = () => {
  return (
    <section className="py-24 bg-white border-y border-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-6">Get started in 3 easy steps</h2>
          <p className="text-lg text-gray-600">Creating content and getting paid is simple. Follow the steps below to get started with our creator network.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-8 relative">
          {/* Step 1 */}
          <div className="relative group">
            <div className="mb-8">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#341368] text-white font-bold text-xl mb-6 shadow-lg shadow-purple-200">1</span>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-700 transition-colors">Create</h3>
              <p className="text-gray-600 mb-2">Film authentic wellness and lifestyle content on your phone, then edit it into a polished final cut in your own style.</p>
              <p className="text-sm font-medium text-gray-400 italic">Need inspiration? We'll share content examples and creative guidance in our Playbook.</p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl aspect-video relative group">
              <img
                src="https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&q=80&w=600"
                alt="Creator Filming Wellness Content"
                className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
            </div>
          </div>

          {/* Step 2 */}
          <div className="relative group">
            <div className="mb-8">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#341368] text-white font-bold text-xl mb-6 shadow-lg shadow-purple-200">2</span>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-700 transition-colors">Submit</h3>
              <p className="text-gray-600 mb-4">Upload your fully edited video along with your raw clips through our creator portal. We handle review, posting, and distribution on our official accounts.</p>

              <ul className="space-y-2 mb-4">
                {["Edited final cut ready for posting", "Raw clips included with your submission", "Vertical video format (9:16)"].map((bullet, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="w-1 h-1 bg-purple-500 rounded-full" />
                    {bullet}
                  </li>
                ))}
              </ul>
              <p className="text-xs font-medium text-gray-400 bg-gray-50 p-3 rounded-lg border border-gray-100 italic">
                You edit your content, we handle everything else — review, captions, scheduling, and posting to our brand channels.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl aspect-video relative group">
              <img
                src="https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=600"
                alt="Submitting Video Content via Mobile App"
                className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
            </div>
          </div>

          {/* Step 3 */}
          <div className="relative group">
            <div className="mb-8">
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#341368] text-white font-bold text-xl mb-6 shadow-lg shadow-purple-200">3</span>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-700 transition-colors">Get paid</h3>
              <p className="text-gray-600 mb-4">Earn a guaranteed monthly base plus performance bonuses based on how your content performs on our official channels.</p>
              <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-purple-700 font-medium">Monthly Base</span> <span className="font-bold text-purple-900">{formatCurrency(PAYMENT_CONFIG.monthlyBase)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-purple-700 font-medium">CPM Bonus ({formatViews(PAYMENT_CONFIG.viewThreshold)}+ views)</span> <span className="font-bold text-purple-900">{formatCurrency(PAYMENT_CONFIG.cpmRate)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-purple-700 font-medium">Max Bonus / Video</span> <span className="font-bold text-purple-900">{formatCurrency(PAYMENT_CONFIG.maxBonusPerVideo)}</span></div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl aspect-video relative flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700">
              {/* Ambient glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-purple-500/15 rounded-full blur-3xl animate-glow" />

              <div className="relative z-10 w-[85%] space-y-3">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Monthly Earnings</span>
                  </div>
                  <div className="flex items-center gap-1 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] font-bold text-emerald-400">Paid</span>
                  </div>
                </div>

                {/* Amount */}
                <div className="text-3xl font-black text-white tracking-tight">$586.00</div>

                {/* Breakdown */}
                <div className="space-y-1.5 pt-1 border-t border-white/5">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Base ({PAYMENT_CONFIG.videosPerMonth} videos)</span>
                    <span className="text-gray-300 font-semibold">{formatCurrency(PAYMENT_CONFIG.monthlyBase)}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Performance bonus (3 videos)</span>
                    <span className="text-emerald-400 font-semibold">+$286.00</span>
                  </div>
                </div>

                {/* Mini bar chart */}
                <div className="flex items-end gap-1 pt-2 h-10">
                  {[35, 50, 28, 65, 45, 80, 60, 72, 55, 90, 68, 85].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-sm bg-gradient-to-t from-purple-500/60 to-purple-400/30"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[9px] text-gray-600">
                  <span>Jan</span>
                  <span>Dec</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes glow {
          0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.2); }
        }
        .animate-glow {
          animation: glow 5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Steps;
